import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  amazonConnectEnabled,
  clearAmazonPendingAuthorize,
  persistAmazonPendingAuthorize,
  readAmazonPendingAuthorize,
} from "../../lib/amazonPendingAuthorize";
import { useResolvedAdvertiserGate } from "./useResolvedAdvertiserGate";

// DEV-2216: Amazon's website authorization workflow sends the seller to our
// Login URI (the console root) after consent, with amazon_callback_uri,
// amazon_state, selling_partner_id and version. The seller may not be signed
// in to Audiohook yet, so AuthGate captures those params before any route
// guard, strips them from the URL and persists them here. Once signed in
// with an advertiser resolved, this hook surfaces them as an explicit
// "Connect Amazon seller account to <advertiser>?" prompt; only the prompt's
// Connect calls amazon-oauth's `authorize`. Mirrors useShopifyPendingConfirm
// (DEV-2332): never automatic, since anyone can send a Login URI link. The
// storage helpers live in src/lib/amazonPendingAuthorize.js.

/**
 * Amazon Login URI capture (DEV-2216), called from AuthGate before every route
 * guard, signed in or not. After the seller consents in Seller Central, Amazon
 * loads the console root with amazon_callback_uri, amazon_state,
 * selling_partner_id and version: the params are persisted (10 minute TTL,
 * Amazon's own limit for the whole flow) and stripped (`stripLoginParams`
 * removes AMAZON_LOGIN_PARAMS), and useAmazonPendingAuthorize surfaces them as
 * an explicit prompt once signed in. Nothing here calls Amazon or
 * amazon-oauth. Blocked storage is reported through `setIntegrationResult`.
 */
export function useAmazonLoginCapture({ setIntegrationResult, stripLoginParams }) {
  const [searchParams] = useSearchParams();
  const capturedStateRef = useRef(null);

  useEffect(() => {
    const amazonCallbackUri = searchParams.get("amazon_callback_uri");
    const amazonState = searchParams.get("amazon_state");
    const sellingPartnerId = searchParams.get("selling_partner_id");
    if (!amazonCallbackUri || !amazonState || !sellingPartnerId) return;

    // Strip on every pass (idempotent), for the same reason as AuthGate's
    // Shopify capture: another one-shot effect may strip its own param off
    // the same snapshot in this commit.
    stripLoginParams();
    // Flag off: drop the request, never store it or report a storage error.
    if (!amazonConnectEnabled()) return;

    if (capturedStateRef.current === amazonState) return;
    capturedStateRef.current = amazonState;

    const stored = persistAmazonPendingAuthorize({
      amazonCallbackUri,
      amazonState,
      sellingPartnerId,
      version: searchParams.get("version"),
    });
    if (!stored) {
      setIntegrationResult({ source: "amazon", status: "error", reason: "amazon_storage_unavailable", confirmToken: null });
    }
  }, [searchParams, stripLoginParams, setIntegrationResult]);
}

/**
 * Surfaces a persisted Login URI request through `openIntegrationsResult`
 * (AuthGate's setIntegrationResult, the same one-shot mechanism that opens the
 * Integrations panel) with status "authorize_prompt". Gated like
 * useShopifyPendingConfirm (useResolvedAdvertiserGate). A support user,
 * masquerading or not, never gets the prompt and keeps the entry: masquerade
 * is read-only and amazon-oauth's `authorize` rejects them, so a surfaced
 * prompt could only sit unrendered in the Integrations panel.
 */
export function useAmazonPendingAuthorize({ openIntegrationsResult }) {
  const { isSupportUser } = useAuth();
  const firedForStateRef = useRef(null);

  const surfacePending = useCallback(() => {
    if (!amazonConnectEnabled()) {
      // Flag off: open nothing, and drop anything stored before it was off.
      clearAmazonPendingAuthorize();
      return;
    }
    if (isSupportUser) return;
    const pending = readAmazonPendingAuthorize();
    if (!pending) {
      // Expired or malformed: drop it so it is never offered again.
      clearAmazonPendingAuthorize();
      return;
    }

    if (firedForStateRef.current === pending.amazonState) return;
    firedForStateRef.current = pending.amazonState;

    openIntegrationsResult({
      source: "amazon",
      status: "authorize_prompt",
      reason: null,
      confirmToken: null,
      amazonLogin: pending,
    });
  }, [openIntegrationsResult, isSupportUser]);

  useResolvedAdvertiserGate(surfacePending);
}
